/**
 * @fileoverview Module for `src/AuthError.js`.
 */

import React, { Component } from 'react';
import Header from './page/Header';
import Footer from './page/Footer';
import { destroyBeacon, initAndOpenBeacon, navigateBeacon } from './Beacon';
import { message } from './lib/deprecated_i18n';
import isNonEmptyString from './lib/functions/isNonEmptyString';
import { loginPageUrl } from './lib/loginProvider';

import propTypes from 'prop-types';
import queryString from 'query-string';
import moment from 'moment';
import { Link } from 'react-router-dom';

/**
 * Builds the support reference code shown on the page and prefilled in the help request.
 *
 * The server omits the request ID when it has no request context, so a missing one is left out rather
 * than rendered as the literal text "undefined" in a code people are asked to quote.
 *
 * @param {string} prefix - The error kind, such as `invalidLogin`.
 * @param {string} [referenceCode] - The request ID from the error response.
 * @returns {string} The prefix, the request ID when present, and a timestamp, joined by hyphens.
 */
function supportReferenceCode(prefix, referenceCode) {
  return [prefix, referenceCode, moment().format('YYYYMMDDhhmmss')].filter(Boolean).join('-');
}

const SUPPORT_EMAIL = 'hello@votefwd.org';

const RESTART_LOGIN_MESSAGE = {
  text: `We couldn’t complete this login. Please start again. If this keeps happening, contact ${ SUPPORT_EMAIL }.`,
  action: 'log-in',
};

/**
 * Messages and next steps (`log-in`, `sign-up` or `support`) for refused sign-ins, following
 * `swing-left/frontend`'s `loginMessages.ts`. `SUPPORT_EMAIL` in a text renders as a link.
 */
const SIGN_IN_FAILURE_MESSAGES = {
  identity_conflict: {
    text: `This login matches more than one Vote Forward account, so we can’t tell which one is yours. Please contact ${ SUPPORT_EMAIL } and we’ll sort it out.`,
    action: 'support',
  },
  email_unverified: {
    text: `This login’s email address isn’t verified, so we can’t connect it to your existing Vote Forward account. Try the login method you used most recently, such as Google, Facebook, or email and password. If that doesn’t work, contact ${ SUPPORT_EMAIL } and we’ll help you log in.`,
    action: 'log-in',
  },
  account_unavailable: {
    text: `This account can’t be used. Please contact ${ SUPPORT_EMAIL } for help.`,
    action: 'support',
  },
  email_required: {
    text: `Your account didn’t share an email address with us. Please sign up with your email address instead, or contact ${ SUPPORT_EMAIL }.`,
    action: 'sign-up',
  },
  nonce_mismatch: RESTART_LOGIN_MESSAGE,
  unsupported_issuer: RESTART_LOGIN_MESSAGE,
};

/** The message for `signin_failed` and any code the page does not know. */
const SIGN_IN_FAILED_MESSAGE = {
  text: `Something went wrong. Please try logging in again. If this keeps happening, contact ${ SUPPORT_EMAIL }.`,
  action: 'log-in',
};

/**
 * Returns the message for a sign-in failure code from the page's query.
 *
 * @param {*} code - The `code` query parameter: a string, or null or an array when malformed.
 * @returns {Object} The code's message when it has one; the generic one otherwise.
 */
function signInFailureMessage(code) {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(SIGN_IN_FAILURE_MESSAGES, code)
    ? SIGN_IN_FAILURE_MESSAGES[code]
    : SIGN_IN_FAILED_MESSAGE;
}

/**
 * Renders a message's text with each mention of the support address as an email link.
 *
 * @param {string} text - Message text.
 * @returns {Array<string|JSX.Element>} Text pieces and links.
 */
function withSupportEmailLinks(text) {
  return text.split(SUPPORT_EMAIL).flatMap((piece, index) => (index === 0
    ? [piece]
    : [<a href={ `mailto:${ SUPPORT_EMAIL }` } key={ index }>{ SUPPORT_EMAIL }</a>, piece]));
}

/**
 * Error page for failed sign-ins and for duplicate or invalid authentication provider flows.
 */
class AuthError extends Component {
  static get propTypes() {
    return {
      auth: propTypes.object,
      location: propTypes.object,
    };
  }

  /** Fixed while the page is mounted; the callback response has no request ID to add. */
  signInReference = supportReferenceCode('signin');

  /**
   * Destroys the Beacon support widget on teardown.
   */
  componentWillUnmount() {
    destroyBeacon();
  }

  /**
   * Opens support Beacon with a prefilled authentication error subject.
   *
   * @param {Event} event
   * @returns {void}
   */
  openAuthErrorBeacon = (event) => {
    const { location, refCode } = event.currentTarget.dataset;
    initAndOpenBeacon({
      auth: this.props.auth,
      beaconArgs: {
        prefill: {
          subject: `Auth Error: ${ refCode }`,
        },
      },
    });
    if (location) {
      navigateBeacon(location);
    }
  };

  /**
   * Renders the page for a sign-in the API refused or that failed some other way.
   *
   * It shows only fixed text, and links to the shared login page with the saved destination, which
   * stays stored for the next sign-in.
   *
   * @param {Object} failure - Entry from `SIGN_IN_FAILURE_MESSAGES`, or `SIGN_IN_FAILED_MESSAGE`.
   * @returns {JSX.Element} The failed sign-in page.
   */
  renderSignInFailure({ text, action }) {
    const refCode = this.signInReference;
    const offersSupport = action === 'support';
    const returnTo = localStorage.getItem('postLoginPath');
    const logInUrl = loginPageUrl({ returnTo });
    return (
      <React.Fragment>
        <Header />
        <div className='row h-50'>
          <div className='col-sm-4 col-10 mx-auto my-5'>
            <h1 className='text-center'>We couldn’t log you in.</h1>
            <p>{ withSupportEmailLinks(text) }</p>
            { action === 'log-in' && (
              <p>
                <a className='btn btn-primary' href={ logInUrl }>Log in again</a>
              </p>
            ) }
            { action === 'sign-up' && (
              <React.Fragment>
                <p>
                  <a className='btn btn-primary' href={ loginPageUrl({ returnTo, register: true }) }>Sign up with email</a>
                </p>
                <p>
                  If you already have a Vote Forward account,{ ' ' }
                  <a href={ logInUrl }>log in</a> the way you did most recently.
                </p>
              </React.Fragment>
            ) }
            <p>
              <button
                className={ offersSupport ? 'btn btn-primary' : 'fw-bold blue-link' }
                data-location='/ask/message/'
                data-ref-code={ refCode }
                id='getHelp'
                onClick={ this.openAuthErrorBeacon }
              >{ offersSupport ? 'Contact us' : message('contact.us') }
              </button>{ ' ' }
              { offersSupport ? 'and mention' : 'for help, and mention' } reference code { refCode }.
            </p>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  /**
   * Renders the auth error page: the reason a sign-in failed, or provider-specific instructions for
   * resolving invalid logins, duplicate accounts or login method mismatches.
   *
   * @returns {JSX.Element} The authentication error page.
   */
  render() {
    const duplicateContext = queryString.parse(this.props.location.search);
    let providerText;
    let duplicateText;
    // A `code`, or no other branch's context, means a failed sign-in: the duplicate check below
    // would match two missing values.
    if (
      duplicateContext.code !== undefined
      || (duplicateContext.invalidLogin !== 'true' && !isNonEmptyString(duplicateContext.duplicate))
    ) {
      return this.renderSignInFailure(signInFailureMessage(duplicateContext.code));
    }
    // Checked before the duplicate branches: without a `duplicate` param, the branches below would show the duplicate-account
    // text (when `provider` is also absent) or the provider-mismatch text.
    if (duplicateContext.invalidLogin === 'true') {
      const refCode = supportReferenceCode('invalidLogin', duplicateContext.referenceCode);
      let loginMethodText;
      if (duplicateContext.provider === 'google-oauth2') {
        loginMethodText = 'by clicking ‘Log in with Google’';
      } else if (duplicateContext.provider === 'facebook') {
        loginMethodText = 'by clicking ‘Log in with Facebook’';
      } else if (duplicateContext.provider === 'auth0') {
        loginMethodText = 'with an email address and password';
      }
      return (
        <React.Fragment>
          <Header />
          <div className='row h-50'>
            <div className='col-sm-4 col-10 mx-auto my-5'>
              <h1 className='text-center'>Whoops!</h1>
              <p>
                We couldn’t match this login to your Vote Forward account.
                { loginMethodText && ` You logged in ${ loginMethodText }.` }
              </p>
              <p>
                Please return to <Link to='/'>our home page</Link> and log in again, choosing the same method you used when you signed up. If that doesn’t work,{ ' ' }
                <button
                  className='fw-bold blue-link'
                  data-location='/ask/message/'
                  data-ref-code={ refCode }
                  id='getHelp'
                  onClick={ this.openAuthErrorBeacon }
                >{ message('contact.us') }
                </button>{ ' ' }
                and mention reference code { refCode }.
              </p>
            </div>
          </div>
          <Footer />
        </React.Fragment>
      );
    }

    if (duplicateContext.duplicate === duplicateContext.provider) {
      const refCode = supportReferenceCode('dupAccount', duplicateContext.referenceCode);
      return (
        <React.Fragment>
          <Header />
          <div className='row h-60'>
            <div className='col-sm-4 col-10 mx-auto my-5 mb-2'>
              <h1 className='text-center'>Whoops!</h1>
              <p>
                It looks like you have a duplicate account in our system. Please either register using a different email address, or{ ' ' }
                <button
                  className='fw-bold blue-link'
                  data-location='/ask/message/'
                  data-ref-code={ refCode }
                  id='getHelp'
                  onClick={ this.openAuthErrorBeacon }
                >{ message('contact.us') }
                </button>.
                { ' ' }{ message('previous.messages.instructions', { email: this.props.auth.authenticatedEmail() }) }{ ' ' }
                <button
                  className='fw-bold blue-link'
                  data-location='/previous-messages/'
                  data-ref-code={ refCode }
                  onClick={ this.openAuthErrorBeacon }
                >{ message('here') }
                </button>.
              </p>
            </div>
          </div>
          <Footer />
        </React.Fragment>
      );
    }

    // Neutral wording for `firebase` and unknown providers: `firebase` does not say how someone signed in.
    if (duplicateContext.provider === 'google-oauth2') {
      providerText = 'clicked ‘Sign up with Google’ or ‘Log in with Google,’';
    } else if (duplicateContext.provider === 'facebook') {
      providerText =
        'clicked ‘Sign up with Facebook’ or ‘Log in with Facebook,’';
    } else if (duplicateContext.provider === 'auth0') {
      providerText = 'attempted to sign up with an email address and password,';
    } else {
      providerText = 'tried to log in,';
    }

    if (duplicateContext.duplicate === 'google-oauth2') {
      duplicateText = 'clicking ‘Log in with Google’';
    } else if (duplicateContext.duplicate === 'facebook') {
      duplicateText = 'clicking ‘Log in with Facebook’';
    } else if (duplicateContext.duplicate === 'auth0') {
      duplicateText = 'providing your email address and password';
    }
    const loginStep = duplicateText ? `Log in by ${ duplicateText }.` : 'Log in the way you did when you made that account.';

    return (
      <React.Fragment>
        <Header />
        <div className='row h-50'>
          <div className='col-sm-4 col-10 mx-auto my-5'>
            <h1 className='text-center'>Whoops!</h1>
            <p>
              We detected a problem, but it’s easy to fix. It looks like you{ ' ' }
              { providerText } but your email address is already registered in our
              system under an account you made previously{ duplicateText && ` by ${ duplicateText }` }.
            </p>
            <p>To gain access to your existing account, please:</p>
            <ol>
              <li>
                Return to <Link to='/'>our home page</Link> and choose to log in.
              </li>
              <li>
                <strong>Important:</strong> If the login page offers to continue
                with an account you didn’t mean to use, choose to use a different
                account instead.
              </li>
              <li>{ loginStep }</li>
            </ol>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }
}

export default AuthError;
